const BASE = '/api';

// Forhindre flere samtidige redirects til /login
let redirectingToLogin = false;
let refreshPromise: Promise<boolean> | null = null;

export function redirectToLogin(): void {
  if (redirectingToLogin) return;
  redirectingToLogin = true;
  // Tøm auth-state (bruker zustand store direkte, unngår sirkulær import)
  try {
    // Siden auth.ts importerer api.ts, kan vi ikke importere auth.ts herfra.
    // Bruk en global event som auth-store lytter på, eller bare redirect.
    window.dispatchEvent(new CustomEvent('auth:logout'));
  } catch {
    // Ignorer – redirect uansett
  }
  // Ikke redirect hvis vi allerede er på login/submit
  const path = window.location.pathname;
  if (path !== '/login' && path !== '/submit') {
    window.location.href = '/login';
  }
}

// Eksportert slik at api-hooks.ts (upload/download) deler samme dedupliserte
// refresh – ellers kan parallelle refresh-kall rotere hverandres token.
export async function refreshSession(): Promise<boolean> {
  if (!refreshPromise) {
    refreshPromise = fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    })
      .then((res) => res.ok)
      .catch(() => false)
      .finally(() => {
        refreshPromise = null;
      });
  }
  return refreshPromise;
}

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers ?? {}),
    },
    ...options,
  });

  if (res.status === 401) {
    // Del én refresh mellom samtidige 401-responser. Refresh-token roteres,
    // så parallelle refresh-kall ellers tilbakekaller hverandres token.
    const refreshed = await refreshSession();
    if (refreshed) {
      const retryRes = await fetch(`${BASE}${path}`, {
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(options.headers ?? {}),
        },
        ...options,
      });
      if (!retryRes.ok) {
        if (retryRes.status === 401) {
          redirectToLogin();
        }
        throw new ApiError(retryRes.status, await safeJson(retryRes));
      }
      return retryRes.json() as Promise<T>;
    }
    // Refresh feilet – redirect til login
    redirectToLogin();
    throw new ApiError(401, { message: 'Ikke innlogget' });
  }

  if (!res.ok) {
    // 403 Forbidden kan også bety at sesjonen er utløpt i noen oppsett
    if (res.status === 403) {
      // Ikke redirect på 403 – det kan være legitim "ingen tilgang"
      // Kun 401 betyr "ikke autentisert"
    }
    throw new ApiError(res.status, await safeJson(res));
  }
  return res.status === 204 ? (undefined as T) : res.json();
}

async function safeJson(res: Response): Promise<any> {
  try {
    return await res.json();
  } catch {
    return { message: res.statusText };
  }
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: any,
  ) {
    super(body?.message ?? `HTTP ${status}`);
    this.name = 'ApiError';
  }
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
};
